import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../config';
import { useAuth } from '../lib/auth';
import Avatar from './Avatar.jsx';
import {
  ChevronDownIcon,
  KeyIcon,
  LogoutIcon,
  UserIcon,
} from './Icons';


/**
 * UserMenu — the top-right "who am I" control in the AppShell.
 * =========================================================
 *
 * Previously the name + avatar were a bare ``<Link to="/me">`` which
 * jumped straight to the profile page.  That worked, but it gave the
 * user no signal that anything else lived behind their name, and
 * "Logout" was only reachable from the left rail (desktop) or the
 * hamburger sheet (mobile) — neither of which is where people
 * instinctively look for it.
 *
 * This component turns the same trigger into a proper dropdown:
 *
 *     ┌──────────────────────────────┐
 *     │  ⬤  Jane Doe                 │   ← header: avatar, name, email
 *     │     jane@acme.com            │
 *     ├──────────────────────────────┤
 *     │  👤  My Profile              │   → /me
 *     │  🔑  Change Password         │   → /me#password
 *     ├──────────────────────────────┤
 *     │  ⏻  Logout                   │   → logout()
 *     └──────────────────────────────┘
 *
 * Behaviour / a11y notes
 * ----------------------
 *   * Trigger is a real ``<button>`` carrying ``aria-haspopup="menu"``
 *     and ``aria-expanded``, so screen readers announce it correctly.
 *   * The panel uses ``role="menu"`` with ``role="menuitem"`` children.
 *   * Closes on outside mousedown, on Escape (returning focus to the
 *     trigger), and on any navigation.
 *   * ArrowDown / ArrowUp move focus between items; Home / End jump to
 *     the first / last.  Opening via keyboard focuses the first item.
 *
 * The outside-click + Escape approach mirrors ``NotificationBell`` so
 * the two top-bar dropdowns behave identically.
 */
export default function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemsRef = useRef([]);

  // ---- derived display bits --------------------------------------------
  // Initials for the avatar fallback: first letter of first + last name,
  // falling back to the first char of the full name / email when a user
  // hasn't filled both fields in.  Mirrors the logic the AppShell used
  // before this component existed.
  const initials = useMemo(() => {
    if (!user) return '?';
    const first = (user.first_name || '').trim();
    const last = (user.last_name || '').trim();
    const combined = (first.charAt(0) + last.charAt(0)).trim();
    if (combined) return combined.toUpperCase();
    const src = (user.full_name || user.email || '?').toString().trim();
    return src.charAt(0).toUpperCase() || '?';
  }, [user]);

  const displayName = user
    ? (user.full_name || user.first_name || user.email || '')
    : '';

  // ---- close on outside-click / Escape ---------------------------------
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Send focus back to the trigger so keyboard users don't get
        // dumped at the top of the document.
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // ---- roving focus between menu items ---------------------------------
  const focusItem = useCallback((index) => {
    const items = itemsRef.current.filter(Boolean);
    if (items.length === 0) return;
    // Wrap around at both ends.
    const next = (index + items.length) % items.length;
    items[next]?.focus();
  }, []);

  const onMenuKeyDown = useCallback(
    (e) => {
      const items = itemsRef.current.filter(Boolean);
      const currentIndex = items.indexOf(document.activeElement);
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          focusItem(currentIndex + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          focusItem(currentIndex - 1);
          break;
        case 'Home':
          e.preventDefault();
          focusItem(0);
          break;
        case 'End':
          e.preventDefault();
          focusItem(items.length - 1);
          break;
        default:
          break;
      }
    },
    [focusItem],
  );

  // ---- actions ----------------------------------------------------------
  const go = useCallback(
    (path) => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const onLogout = useCallback(() => {
    setOpen(false);
    logout();
  }, [logout]);

  // Opening from the keyboard (Enter / Space / ArrowDown) should land
  // focus on the first item; opening by mouse should not steal focus.
  const openWithFocus = useCallback(() => {
    setOpen(true);
    // Defer until the panel has actually mounted.
    window.requestAnimationFrame(() => focusItem(0));
  }, [focusItem]);

  const onTriggerKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        openWithFocus();
      }
    },
    [openWithFocus],
  );

  if (!user) return null;

  return (
    <div className="ty-user" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="ty-user__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${displayName}`}
        title="Account, profile & settings"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="ty-user__name">{displayName}</span>
        <Avatar
          initials={initials}
          imageUrl={user.profile_image_url || ''}
          name={displayName}
          size={30}
        />
        <span className="ty-user__caret" aria-hidden>
          <ChevronDownIcon size={14} />
        </span>
      </button>

      {open && (
        <div
          className="ty-user__menu"
          role="menu"
          aria-label="Account menu"
          onKeyDown={onMenuKeyDown}
        >
          {/* Identity header — confirms *which* account is signed in.
              Handy on shared machines and for anyone juggling a
              personal + admin login. */}
          <div className="ty-user__menu-head">
            <Avatar
              initials={initials}
              imageUrl={user.profile_image_url || ''}
              name={displayName}
              size={38}
            />
            <div className="ty-user__menu-id">
              <span className="ty-user__menu-name">{displayName}</span>
              {user.email && (
                <span className="ty-user__menu-email">{user.email}</span>
              )}
            </div>
          </div>

          <div className="ty-user__menu-sep" role="separator" />

          <button
            type="button"
            role="menuitem"
            ref={(el) => { itemsRef.current[0] = el; }}
            className="ty-user__menu-item"
            onClick={() => go(ROUTES.PROFILE)}
          >
            <span className="ty-user__menu-icon" aria-hidden>
              <UserIcon size={17} />
            </span>
            <span className="ty-user__menu-label">
              My Profile
              <span className="ty-user__menu-hint">
                Name, email, phone &amp; picture
              </span>
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            ref={(el) => { itemsRef.current[1] = el; }}
            className="ty-user__menu-item"
            /* Deep-link straight to the password card on the profile
               page — ``ProfilePage`` reads this hash and scrolls /
               focuses that section. */
            onClick={() => go(`${ROUTES.PROFILE}#password`)}
          >
            <span className="ty-user__menu-icon" aria-hidden>
              <KeyIcon size={17} />
            </span>
            <span className="ty-user__menu-label">
              Change Password
              <span className="ty-user__menu-hint">
                Requires your current password
              </span>
            </span>
          </button>

          <div className="ty-user__menu-sep" role="separator" />

          <button
            type="button"
            role="menuitem"
            ref={(el) => { itemsRef.current[2] = el; }}
            className="ty-user__menu-item ty-user__menu-item--danger"
            onClick={onLogout}
          >
            <span className="ty-user__menu-icon" aria-hidden>
              <LogoutIcon size={17} />
            </span>
            <span className="ty-user__menu-label">Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}
